/**
 * GameColorContext · 游戏图例颜色 + 元信息透传上下文
 *
 * 设计目的：
 * - 解决游戏颜色映射 / 游戏元信息需要跨多层组件传递的问题
 * - 通过 Context 实现"透传"，避免 props drilling
 * - 所有图表子组件通过 `useGameColorContext()` 直接获取颜色映射和 gameMap
 *
 * 使用方式：
 * ```tsx
 * // 在页面顶层包裹 Provider
 * <GameColorProvider gameIds={filter.game_ids}>
 *   <ChartComponent />
 * </GameColorProvider>
 *
 * // 图表组件中消费
 * function ChartComponent() {
 *   const { gameColorMap, gameMap, getGameColor } = useGameColorContext();
 *   const color = getGameColor('game_123');
 *   const game = gameMap['game_123'];
 * }
 * ```
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useGameColorMap, TGameColorMap } from '../hooks/useGameColorMap';
import { useGameMap, TGameMap } from '../hooks/useGameMap';

/** Context 值类型 */
export interface IGameColorContextValue {
  /** game_id → 颜色的映射表 */
  gameColorMap: TGameColorMap;
  /** game_id → 游戏元信息映射（cond.materials_config_games） */
  gameMap: TGameMap;
  /** gameMap 是否正在加载 */
  gameMapLoading: boolean;
  /** 获取指定游戏的图例颜色 */
  getGameColor: (gameId: string) => string;
}

/** 默认值：空映射 */
const defaultContextValue: IGameColorContextValue = {
  gameColorMap: {},
  gameMap: {},
  gameMapLoading: false,
  getGameColor: () => '#5086f3',
};

const GameColorContext = createContext<IGameColorContextValue>(defaultContextValue);

/** Provider Props */
export interface IGameColorProviderProps {
  /** 当前选中的游戏 ID 列表 */
  gameIds: string[];
  children: React.ReactNode;
}

/**
 * 游戏颜色 + 元信息透传 Provider
 *
 * 在页面顶层包裹，根据 gameIds 自动计算颜色映射 + 拉取游戏元信息，统一下发
 */
export function GameColorProvider({ gameIds, children }: IGameColorProviderProps) {
  const { gameColorMap, getGameColor } = useGameColorMap(gameIds);
  const { gameMap, loading: gameMapLoading } = useGameMap();

  const value = useMemo<IGameColorContextValue>(
    () => ({ gameColorMap, gameMap, gameMapLoading, getGameColor }),
    [gameColorMap, gameMap, gameMapLoading, getGameColor],
  );

  return (
    <GameColorContext.Provider value={value}>
      {children}
    </GameColorContext.Provider>
  );
}

/**
 * 消费游戏颜色映射的 Hook
 *
 * @returns 包含 gameColorMap 和 getGameColor 的 context 值
 *
 * @example
 * ```tsx
 * const { getGameColor } = useGameColorContext();
 * const color = getGameColor(gameId);
 * ```
 */
export function useGameColorContext(): IGameColorContextValue {
  return useContext(GameColorContext);
}

export default GameColorContext;
